/**
 * The renderer for the api's guide block model (`modules/developers/guides.ts`).
 *
 * The same blocks come out as Markdown in `reference.md` and `llms.txt`; this is the React
 * half, so *Getting started* and *Migrating from monday.com* have exactly one source of words.
 *
 * The inline grammar is deliberately two rules — `` `code` `` and `**strong**` — not a Markdown
 * parser. Everything rendered here is written by us, in one file we control, and pulling a
 * parser in to read our own strings would add a dependency and an HTML-injection surface for
 * emphasis nobody asked for.
 */
import type * as React from 'react';
import type { Guide, GuideBlock } from './source';

const INLINE = /(`[^`]+`|\*\*[^*]+\*\*)/g;

export function InlineText({ text }: { text: string }): React.ReactElement {
  const parts = text.split(INLINE).filter((part) => part.length > 0);
  return (
    <>
      {parts.map((part, index) => {
        const key = `${index}-${part.slice(0, 8)}`;
        if (part.startsWith('`') && part.endsWith('`') && part.length > 1) {
          return <code key={key}>{part.slice(1, -1)}</code>;
        }
        if (part.startsWith('**') && part.endsWith('**') && part.length > 3) {
          return <strong key={key}>{part.slice(2, -2)}</strong>;
        }
        return <span key={key}>{part}</span>;
      })}
    </>
  );
}

export function CodeBlock({
  code,
  title,
  testId,
}: {
  code: string;
  title?: string | null;
  testId?: string;
}): React.ReactElement {
  return (
    <>
      {title ? <p className="ez-guide__code-title">{title}</p> : null}
      <pre className="ez-docs__code" data-testid={testId}>
        {code}
      </pre>
    </>
  );
}

function Block({ block }: { block: GuideBlock }): React.ReactElement | null {
  switch (block.kind) {
    case 'p':
      return (
        <p className="ez-docs__prose">
          <InlineText text={block.text} />
        </p>
      );
    case 'note':
      return (
        <p className="ez-dev__note" role="note">
          <InlineText text={block.text} />
        </p>
      );
    case 'list':
      return (
        <ul>
          {block.items.map((item) => (
            <li key={item.slice(0, 48)}>
              <InlineText text={item} />
            </li>
          ))}
        </ul>
      );
    case 'code':
      return <CodeBlock code={block.code} title={block.title} />;
    case 'table':
      return (
        <div className="ez-docs__table-wrap">
          <table className="ez-docs__table">
            <thead>
              <tr>
                {block.head.map((cell, index) => (
                  <th key={`${index}-${cell}`}>{cell}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={`${rowIndex}-${row[0] ?? ''}`}>
                  {row.map((cell, cellIndex) => (
                    <td key={`${cellIndex}-${cell.slice(0, 24)}`}>
                      <InlineText text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

export function GuideView({ guide }: { guide: Guide }): React.ReactElement {
  return (
    <article className="ez-guide" data-testid={`developers-guide-${guide.id}`}>
      <h1 className="ez-dev__heading">{guide.title}</h1>
      <p className="ez-guide__lede">{guide.lede}</p>
      {guide.sections.map((section) => (
        <section key={section.id} id={section.id} data-testid={`guide-section-${section.id}`}>
          <h2>{section.title}</h2>
          {section.blocks.map((block, index) => (
            <Block key={`${section.id}-${index}`} block={block} />
          ))}
        </section>
      ))}
    </article>
  );
}
